import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  IconMail, IconMailOpened, IconSend, IconTrash, IconPencil,
  IconRefresh, IconChevronLeft, IconX, IconSearch, IconUser,
  IconInbox, IconArrowLeft, IconPaperclip, IconFile,
  IconArchive, IconArchiveOff,
} from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import api from '../api/api';
import './MessagePage.css';

/** 파일 크기 포맷 */
const formatSize = (bytes) => {
  if (!bytes) return '0B';
  if (bytes < 1024) return bytes + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
};

/* ── 에디터 (textarea) ── */
const RichEditor = ({ onChange, placeholder = '쪽지 내용을 입력하세요' }) => (
  <textarea
    className="form-control form-control-sm"
    rows={8}
    placeholder={placeholder}
    onChange={e => onChange(e.target.value)}
    style={{ fontSize: 14 }}
  />
);

/** HTML 렌더링 (구형 plain-text 호환, base64 이미지 허용) */
const PURIFY_CONFIG = { ADD_DATA_URI_TAGS: ['img'] };
const renderContent = (content) => {
  if (!content) return '';
  const t = content.trim();
  if (t.startsWith('<')) return DOMPurify.sanitize(t, PURIFY_CONFIG);
  return DOMPurify.sanitize('<p>' + t.replace(/\n/g, '<br>') + '</p>', PURIFY_CONFIG);
};

export default function MessagePage({ me, showToast, onUnreadChange }) {
  const [activeBox, setActiveBox]     = useState('inbox'); // 'inbox' | 'sent' | 'archived'
  const [messages, setMessages]       = useState([]);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage]               = useState(0);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);   // 선택된 쪽지 상세
  const [loadingDetail, setLoadingDetail] = useState(false);

  /* 쪽지 쓰기 모달 */
  const [composeOpen, setComposeOpen] = useState(false);
  const [toQuery, setToQuery]         = useState('');
  const [toUsers, setToUsers]         = useState([]);   // [{ id, name, email, role }, ...]
  const [userResults, setUserResults] = useState([]);
  const [searchingUser, setSearchingUser] = useState(false);
  const [title, setTitle]             = useState('');
  const [content, setContent]         = useState('');
  const [sending, setSending]         = useState(false);

  /* 파일 첨부 */
  const [attachFiles, setAttachFiles] = useState([]); // { tempId, name, size, id, uploading, error }
  const fileInputRef = useRef(null);

  /* 에디터 리마운트 키 (모달 열 때마다 초기화) */
  const [composeKey, setComposeKey] = useState(0);

  const searchTimer = useRef(null);

  /* ── 목록 조회 ── */
  const fetchMessages = useCallback(async (box = activeBox, p = 0) => {
    setLoading(true);
    setSelected(null);
    try {
      const url = box === 'inbox' ? '/messages/inbox'
                : box === 'archived' ? '/messages/archived'
                : '/messages/sent';
      const res = await api.get(url, { params: { page: p, size: 20 } });
      setMessages(res.data.messages);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
      setPage(p);
    } catch {
      showToast('쪽지 목록 조회 실패', 'danger');
    } finally {
      setLoading(false);
    }
  }, [activeBox, showToast]);

  useEffect(() => {
    fetchMessages(activeBox, 0);
  }, [activeBox]);

  /* ── 상세 조회 ── */
  const handleSelect = async (msg) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/messages/${msg.id}`);
      setSelected(res.data);
      // 받은함이고 안읽은 쪽지였으면 목록 갱신 + 뱃지 갱신
      if (activeBox === 'inbox' && !msg.isRead) {
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isRead: true } : m));
        onUnreadChange?.();
      }
    } catch {
      showToast('쪽지 조회 실패', 'danger');
    } finally {
      setLoadingDetail(false);
    }
  };

  /* ── 삭제 ── */
  const handleDelete = async (id) => {
    if (!window.confirm('이 쪽지를 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/messages/${id}`);
      showToast('삭제되었습니다.');
      setSelected(null);
      fetchMessages(activeBox, page);
      if (activeBox === 'inbox') onUnreadChange?.();
    } catch {
      showToast('삭제 실패', 'danger');
    }
  };

  /* ── 보관 / 보관해제 ── */
  const handleArchive = async (id, currentlyArchived) => {
    try {
      await api.post(`/messages/${id}/archive`);
      if (currentlyArchived) {
        showToast('보관이 해제되었습니다.');
      } else {
        showToast('쪽지를 보관했습니다.');
      }
      setSelected(null);
      fetchMessages(activeBox, page);
    } catch {
      showToast('처리 실패', 'danger');
    }
  };

  /* ── 수신자 검색 (디바운스 300ms) ── */
  const handleToQueryChange = (e) => {
    const v = e.target.value;
    setToQuery(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!v.trim()) { setUserResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchingUser(true);
      try {
        const res = await api.get('/messages/users/search', { params: { keyword: v } });
        // 이미 선택된 사용자 제외
        const selectedIds = toUsers.map(u => u.id);
        setUserResults(res.data.filter(u => !selectedIds.includes(u.id)));
      } catch {
        setUserResults([]);
      } finally {
        setSearchingUser(false);
      }
    }, 300);
  };

  /* ── 파일 선택 → 즉시 업로드 ── */
  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files);
    e.target.value = ''; // 같은 파일 재선택 가능하도록 초기화
    for (const file of selected) {
      if (file.size > 10 * 1024 * 1024) {
        showToast(`"${file.name}" 파일이 10MB를 초과합니다.`, 'warning');
        continue;
      }
      const tempId = Date.now() + Math.random();
      setAttachFiles(prev => [...prev, { tempId, name: file.name, size: file.size, uploading: true }]);
      try {
        const formData = new FormData();
        formData.append('files', file);
        const res = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploaded = res.data[0];
        setAttachFiles(prev => prev.map(f =>
          f.tempId === tempId ? { ...f, id: uploaded.id, uploading: false } : f
        ));
      } catch {
        setAttachFiles(prev => prev.map(f =>
          f.tempId === tempId ? { ...f, uploading: false, error: '업로드 실패' } : f
        ));
        showToast(`"${file.name}" 업로드에 실패했습니다.`, 'danger');
      }
    }
  };

  /* ── 첨부파일 다운로드 (Authorization 헤더 포함) ── */
  const handleDownload = async (fileId, fileName) => {
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast('파일 다운로드 실패', 'danger');
    }
  };

  /* ── 첨부파일 제거 ── */
  const removeAttach = async (tempId) => {
    const target = attachFiles.find(f => f.tempId === tempId);
    if (target?.id) {
      // 서버에서도 삭제
      try { await api.delete(`/files/${target.id}`); } catch { /* 무시 */ }
    }
    setAttachFiles(prev => prev.filter(f => f.tempId !== tempId));
  };

  /* ── 쪽지 전송 ── */
  const handleSend = async (e) => {
    e.preventDefault();
    if (toUsers.length === 0) { showToast('수신자를 선택해주세요.', 'danger'); return; }
    if (!title.trim()) { showToast('제목을 입력해주세요.', 'danger'); return; }
    if (!content || !content.replace(/<[^>]*>/g, '').trim()) {
      showToast('내용을 입력해주세요.', 'danger'); return;
    }
    // 업로드 중인 파일이 있으면 대기
    if (attachFiles.some(f => f.uploading)) {
      showToast('파일 업로드가 완료될 때까지 기다려주세요.', 'warning');
      return;
    }
    setSending(true);
    try {
      const attachmentIds = attachFiles.filter(f => f.id).map(f => f.id);
      await api.post('/messages', {
        receiverIds: toUsers.map(u => u.id),
        title: title.trim(),
        content: content.trim(),
        attachmentIds,
      });
      const names = toUsers.map(u => u.name || u.email).join(', ');
      showToast(`${names}님께 쪽지를 보냈습니다.`);
      setComposeOpen(false);
      resetCompose();
      if (activeBox === 'sent') fetchMessages('sent', 0);
    } catch (err) {
      showToast(err.response?.data?.error || '전송 실패', 'danger');
    } finally {
      setSending(false);
    }
  };

  const resetCompose = () => {
    setToQuery(''); setToUsers([]); setUserResults([]);
    setTitle(''); setContent('');
    setAttachFiles([]);
    setComposeKey(k => k + 1);
  };

  const fmt = (v) => v ? v.replace('T', ' ').slice(0, 16) : '-';

  /* ── 역할 뱃지 색상 ── */
  const roleBadge = (role) => {
    if (role === 'MASTER') return 'bg-danger';
    if (role === 'ADMIN')  return 'bg-purple';
    return 'bg-azure';
  };

  return (
    <div className="page-body">
      <div className="container-xl">

        {/* 헤더 */}
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <div className="text-muted small">커뮤니케이션</div>
            <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>쪽지함</h2>
          </div>
          <button className="btn btn-sm btn-primary d-flex align-items-center gap-2"
            onClick={() => { resetCompose(); setComposeOpen(true); }}>
            <IconPencil size={16} /> 쪽지 쓰기
          </button>
        </div>

        <div className="row g-3">

          {/* ── 좌측: 탭 + 목록 ── */}
          <div className="col-12 col-md-5 col-lg-4">
            <div className="card h-100">
              {/* 받은함 / 보낸함 탭 + 새로고침 */}
              <div className="card-header" style={{ padding: '0 12px' }}>
                <div className="d-flex align-items-center justify-content-between w-100">
                  {/* 탭 */}
                  <div style={{ display: 'flex', gap: 0, alignItems: 'flex-end' }}>
                    {[
                      { id: 'inbox',    label: '받은함', icon: <IconInbox   size={14} /> },
                      { id: 'sent',     label: '보낸함', icon: <IconSend    size={14} /> },
                      { id: 'archived', label: '보관함', icon: <IconArchive size={14} /> },
                    ].map(tab => {
                      const isActive = activeBox === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => { setActiveBox(tab.id); setPage(0); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '10px 14px', cursor: 'pointer', border: 'none',
                            background: 'none', fontSize: 13, fontWeight: isActive ? 600 : 400,
                            color: isActive ? 'var(--tblr-primary, #206bc4)' : 'var(--tblr-muted, #9aa0ac)',
                            borderBottom: isActive
                              ? '2px solid var(--tblr-primary, #206bc4)'
                              : '2px solid transparent',
                            marginBottom: -1,
                          }}
                        >
                          {tab.icon} {tab.label}
                          {isActive && totalElements > 0 && (
                            <span className="badge bg-blue-lt text-blue ms-1" style={{ fontSize: 10, padding: '2px 6px' }}>
                              {totalElements}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* 새로고침 */}
                  <button className="btn btn-sm btn-ghost-secondary p-1"
                    onClick={() => fetchMessages(activeBox, page)}>
                    <IconRefresh size={15} />
                  </button>
                </div>
              </div>

              {/* 쪽지 목록 */}
              <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: 560 }}>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <IconMailOpened size={40} strokeWidth={1} className="mb-2 d-block mx-auto" />
                    쪽지가 없습니다.
                  </div>
                ) : messages.map(msg => {
                  const isActive  = selected?.id === msg.id;
                  const isUnread  = activeBox === 'inbox' && !msg.isRead;
                  const personName = activeBox === 'inbox' ? msg.senderName : msg.receiverName;
                  const personNick = activeBox === 'inbox' ? msg.senderNickname : msg.receiverNickname;
                  const person = personNick ? `${personName}(${personNick})` : personName;
                  const personEmail = activeBox === 'inbox' ? msg.senderEmail : msg.receiverEmail;
                  return (
                    <button
                      key={msg.id}
                      className={`list-group-item list-group-item-action text-start${isActive ? ' active' : ''}`}
                      onClick={() => handleSelect(msg)}
                      style={{
                        borderLeft: isUnread ? '3px solid var(--tblr-primary)' : '3px solid transparent',
                        background: isUnread && !isActive ? 'rgba(32,107,196,0.04)' : undefined,
                      }}
                    >
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span style={{
                          background: isActive ? '#fff3' : '#206bc4',
                          color: isActive ? '#fff' : '#fff',
                          borderRadius: '50%', width: 28, height: 28,
                          display: 'inline-flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 12,
                          fontWeight: 700, flexShrink: 0,
                        }}>
                          {(person || personEmail || '?')[0].toUpperCase()}
                        </span>
                        <div className="flex-fill overflow-hidden">
                          <div className="d-flex align-items-center justify-content-between">
                            <span className={`text-truncate${isUnread ? ' fw-bold' : ''}`}
                              style={{ fontSize: 13, maxWidth: 120 }}>
                              {person || personEmail}
                            </span>
                            <span style={{ fontSize: 11, opacity: 0.65, flexShrink: 0 }}>
                              {fmt(msg.createdAt)}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-1">
                            {isUnread && (
                              <span className="badge bg-blue text-white" style={{ fontSize: 9, padding: '2px 5px' }}>NEW</span>
                            )}
                            {msg.attachmentCount > 0 && (
                              <IconPaperclip size={11} className={isActive ? 'text-white' : 'text-muted'} />
                            )}
                            <span className="text-truncate" style={{ fontSize: 12 }}>
                              {msg.title}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* 페이징 */}
              {totalPages > 1 && (
                <div className="card-footer d-flex justify-content-center gap-1 py-2">
                  <button className="btn btn-sm btn-ghost-secondary"
                    disabled={page === 0}
                    onClick={() => fetchMessages(activeBox, page - 1)}>
                    <IconChevronLeft size={14} />
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button key={i}
                      className={`btn btn-sm ${page === i ? 'btn-primary' : 'btn-ghost-secondary'}`}
                      onClick={() => fetchMessages(activeBox, i)}>
                      {i + 1}
                    </button>
                  ))}
                  <button className="btn btn-sm btn-ghost-secondary"
                    disabled={page === totalPages - 1}
                    onClick={() => fetchMessages(activeBox, page + 1)}>
                    <IconChevronLeft size={14} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── 우측: 쪽지 상세 ── */}
          <div className="col-12 col-md-7 col-lg-8">
            <div className="card h-100" style={{ minHeight: 400 }}>
              {loadingDetail ? (
                <div className="d-flex align-items-center justify-content-center h-100 py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : !selected ? (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 py-5 text-muted">
                  <IconMail size={60} strokeWidth={1} className="mb-3" />
                  <div>왼쪽 목록에서 쪽지를 선택하세요</div>
                </div>
              ) : (
                <>
                  <div className="card-header">
                    <div className="d-flex align-items-start justify-content-between w-100">
                      <div>
                        <h3 className="card-title mb-1">{selected.title}</h3>
                        <div className="text-muted small d-flex gap-3">
                          <span>
                            <strong>{activeBox === 'inbox' ? '보낸 사람' : '받는 사람'}:</strong>&nbsp;
                            {activeBox === 'inbox'
                              ? (selected.senderName || selected.senderEmail) + (selected.senderNickname ? ` (${selected.senderNickname})` : '')
                              : (selected.receiverName || selected.receiverEmail) + (selected.receiverNickname ? ` (${selected.receiverNickname})` : '')}
                          </span>
                          <span><strong>시각:</strong> {fmt(selected.createdAt)}</span>
                        </div>
                      </div>
                      <div className="d-flex gap-2 ms-3 flex-shrink-0">
                        {/* 받은 쪽지면 답장 버튼 */}
                        {activeBox === 'inbox' && (
                          <button
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                            onClick={() => {
                              resetCompose();
                              setToUsers([{ id: selected.senderId, name: selected.senderName, email: selected.senderEmail }]);
                              setTitle(`Re: ${selected.title}`);
                              setComposeOpen(true);
                            }}
                          >
                            <IconArrowLeft size={14} /> 답장
                          </button>
                        )}
                        {/* 받은함 → 보관 / 보관함 → 보관해제 */}
                        {(activeBox === 'inbox' || activeBox === 'archived') && (
                          <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                            onClick={() => handleArchive(selected.id, activeBox === 'archived')}
                          >
                            {activeBox === 'archived'
                              ? <><IconArchiveOff size={14} /> 보관해제</>
                              : <><IconArchive    size={14} /> 보관</>
                            }
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                          onClick={() => handleDelete(selected.id)}
                        >
                          <IconTrash size={14} /> 삭제
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="message-content"
                      style={{ lineHeight: 1.8, fontSize: 15, minHeight: 200 }}
                      dangerouslySetInnerHTML={{ __html: renderContent(selected.content) }}
                    />

                    {/* ── 첨부파일 ── */}
                    {selected.attachments?.length > 0 && (
                      <div className="mt-4 pt-3 border-top">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="fw-semibold d-flex align-items-center gap-1 text-muted"
                            style={{ fontSize: 13 }}>
                            <IconPaperclip size={14} />
                            첨부파일 {selected.attachments.length}개
                          </div>
                          {selected.attachments.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost-secondary d-flex align-items-center gap-1"
                              style={{ fontSize: 12 }}
                              onClick={() => selected.attachments.forEach(f => handleDownload(f.id, f.originalName))}
                            >
                              <IconPaperclip size={13} /> 전체 다운로드
                            </button>
                          )}
                        </div>
                        {selected.attachments.map(f => (
                          <button
                            key={f.id}
                            type="button"
                            className="d-flex align-items-center gap-2 text-decoration-none mb-1 p-2 border rounded w-100"
                            style={{ color: 'inherit', background: 'none', cursor: 'pointer', transition: 'background 0.1s', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f4f6fb'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                            onClick={() => handleDownload(f.id, f.originalName)}
                          >
                            <IconFile size={16} className="text-muted flex-shrink-0" />
                            <span className="flex-fill text-truncate" style={{ fontSize: 13 }}>
                              {f.originalName}
                            </span>
                            <small className="text-muted flex-shrink-0">{formatSize(f.fileSize)}</small>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ══ 쪽지 쓰기 모달 ══ */}
      {composeOpen && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.45)', zIndex: 5000 }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <form onSubmit={handleSend}>
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center gap-2">
                    <IconPencil size={18} /> 쪽지 쓰기
                  </h5>
                  <button type="button" className="btn-close"
                    onClick={() => { setComposeOpen(false); resetCompose(); }} />
                </div>

                <div className="modal-body">

                  {/* 수신자 (다중 선택) */}
                  <div className="mb-3 position-relative">
                    <label className="form-label fw-semibold">
                      <IconUser size={14} className="me-1" />수신자
                      {toUsers.length > 0 && (
                        <span className="badge bg-blue-lt text-blue ms-2" style={{ fontSize: 10 }}>
                          {toUsers.length}명
                        </span>
                      )}
                    </label>
                    {/* 선택된 수신자 태그 */}
                    {toUsers.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {toUsers.map(u => (
                          <span key={u.id} className="badge bg-blue-lt text-blue d-inline-flex align-items-center gap-1"
                            style={{ fontSize: 12, padding: '5px 8px', borderRadius: 16 }}>
                            <span style={{
                              background: '#206bc4', color: '#fff', borderRadius: '50%',
                              width: 18, height: 18, display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center',
                              fontSize: 9, fontWeight: 700, flexShrink: 0,
                            }}>
                              {(u.name || u.email)[0].toUpperCase()}
                            </span>
                            {u.nickname ? `${u.name}(${u.nickname})` : (u.name || u.email)}
                            <button type="button"
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', color: 'inherit', opacity: 0.7 }}
                              onClick={() => setToUsers(prev => prev.filter(p => p.id !== u.id))}>
                              <IconX size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* 검색 입력 */}
                    <div className="input-group">
                      <span className="input-group-text">
                        <IconSearch size={15} className="text-muted" />
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="이름 또는 이메일로 검색하여 추가..."
                        value={toQuery}
                        onChange={handleToQueryChange}
                        autoComplete="off"
                      />
                      {searchingUser && (
                        <span className="input-group-text">
                          <span className="spinner-border spinner-border-sm" />
                        </span>
                      )}
                    </div>
                    {/* 검색 결과 드롭다운 */}
                    {userResults.length > 0 && (
                      <div style={{
                        position: 'absolute', zIndex: 100, width: '100%',
                        background: '#fff', border: '1px solid #e6e7e9',
                        borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        top: '100%', left: 0, maxHeight: 200, overflowY: 'auto',
                      }}>
                        {userResults.map(u => (
                          <button key={u.id} type="button"
                            className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                            onClick={() => {
                              setToUsers(prev => [...prev, u]);
                              setToQuery('');
                              setUserResults([]);
                            }}>
                            <span style={{
                              background: '#206bc4', color: '#fff', borderRadius: '50%',
                              width: 26, height: 26, display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center',
                              fontSize: 11, fontWeight: 700, flexShrink: 0,
                            }}>
                              {(u.name || u.email)[0].toUpperCase()}
                            </span>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>
                                {u.name || '-'}
                                {u.nickname && <span className="fw-normal text-muted ms-1" style={{ fontSize: 11 }}>({u.nickname})</span>}
                              </div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{u.email}</div>
                            </div>
                            <span className={`badge ms-auto ${roleBadge(u.role)}`} style={{ fontSize: 10 }}>
                              {u.role}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 제목 */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">제목</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="쪽지 제목을 입력하세요"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      maxLength={200}
                      required
                    />
                  </div>

                  {/* 내용 */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">내용</label>
                    <RichEditor key={composeKey} onChange={setContent} />
                  </div>

                  {/* ── 파일 첨부 ── */}
                  <div className="mb-1">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="form-label fw-semibold mb-0" style={{ fontSize: 14 }}>
                        <IconPaperclip size={14} className="me-1" />파일 첨부
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={attachFiles.filter(f => f.id).length >= 5}
                      >
                        <IconFile size={13} /> 파일 선택
                      </button>
                      <span className="text-muted small">최대 5개 · 10MB 이하</span>
                    </div>
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    {/* 첨부파일 목록 */}
                    {attachFiles.length > 0 && (
                      <div className="border rounded p-2" style={{ background: '#f8f9fa' }}>
                        {attachFiles.map(f => (
                          <div key={f.tempId}
                            className="d-flex align-items-center gap-2 py-1 px-1"
                            style={{ borderBottom: '1px solid #eee', lastChild: { borderBottom: 'none' } }}>
                            <IconFile size={14} className="text-muted flex-shrink-0" />
                            <span className="flex-fill text-truncate" style={{ fontSize: 13 }}>{f.name}</span>
                            <small className="text-muted flex-shrink-0">{formatSize(f.size)}</small>
                            {f.uploading && (
                              <span className="spinner-border spinner-border-sm text-primary flex-shrink-0"
                                style={{ width: 14, height: 14 }} />
                            )}
                            {f.error && (
                              <span className="badge bg-danger-lt text-danger flex-shrink-0"
                                style={{ fontSize: 10 }}>
                                실패
                              </span>
                            )}
                            {!f.uploading && (
                              <button type="button"
                                className="btn-close flex-shrink-0"
                                style={{ fontSize: 10 }}
                                onClick={() => removeAttach(f.tempId)}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setComposeOpen(false); resetCompose(); }}>
                    취소
                  </button>
                  <button type="submit" className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                    disabled={sending || toUsers.length === 0 || attachFiles.some(f => f.uploading)}>
                    {sending
                      ? <span className="spinner-border spinner-border-sm" />
                      : <IconSend size={15} />}
                    보내기
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
